import { useEffect, useState } from 'react';
import { MessageCircle, Menu } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { getMyProfile, type BuyerProfile } from '@/services/clientService';

interface HeaderProps {
  onToggleMobileMenu?: () => void;
}

export default function Header({ onToggleMobileMenu }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuthStore();
  const { unreadCount } = useChatStore();

  const [clientProfile, setClientProfile] = useState<BuyerProfile | null>(null);

  const handleOpenChat = () => {
    const isClientPortal = location.pathname.startsWith('/client');
    navigate(isClientPortal ? '/client/chat' : '/chat');
  };

  useEffect(() => {
    const isClientPortal = location.pathname.startsWith('/client');
    if (!isClientPortal) {
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const profile = await getMyProfile();
        if (!cancelled) {
          setClientProfile(profile);
          // If we have a user in auth store, update it if names don't match
          if (user && (profile.first_name || profile.last_name)) {
            const nextFirst = profile.first_name || user.first_name;
            const nextLast = profile.last_name || user.last_name;
            if (nextFirst !== user.first_name || nextLast !== user.last_name) {
              setUser({
                ...user,
                first_name: nextFirst,
                last_name: nextLast
              });
            }
          }
        }
      } catch (error) {
        console.error('Erreur chargement profil Client (Header):', error);
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const isClientPortal = location.pathname.startsWith('/client');

  const displayName = isClientPortal
    ? user?.first_name || clientProfile?.first_name || clientProfile?.company_name || user?.email || ''
    : user?.first_name || user?.email || '';

  return (
    <header className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-md border-b border-slate-200/70 dark:border-slate-700/60 shadow-[0_10px_20px_-12px_rgba(2,6,23,0.15)]">
      <div className="mx-auto flex h-12 items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 h-full">
          {onToggleMobileMenu && (
            <button
              type="button"
              onClick={onToggleMobileMenu}
              className="md:hidden inline-flex items-center justify-center rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          <div className="flex items-center h-full border-b-[3px] border-green-700">
            <p className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">
              Bienvenue, {displayName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 h-full">
          <div className="hidden sm:flex h-full items-center border-b-[3px] border-green-700">
            <p className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">
              NOVA est là
            </p>
          </div>
          <button
            type="button"
            onClick={handleOpenChat}
            className="relative inline-flex items-center justify-center rounded-full p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-900/40 transition-colors"
            title="Ouvrir la messagerie"
          >
            <MessageCircle className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-900">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
